import { Badge, Box, Divider, HStack, Stack, Text, VStack, Link } from '@chakra-ui/react'
import { StepCircle } from './StepCircle'
import { FILENAMES, COMMENT_STATUS, STEPS } from '@/shared/constants'
import { ICourse } from '@/shared/ICourse'
import { ICourseModule } from '@/shared/ICourseModule'
import { WorkflowService } from '@/shared/WorkflowService'
import { formatDateFromNow } from '@/shared/parse'
import { CourseStepFileList } from './CourseStepFileList'
import { CommentSetBadge } from '@/app/dashboard/CommentSetBadge'
import { IComment } from '@/shared/IComment'
import { AuthService } from '@/app/services/AuthService'
import { Link as RouterLink } from 'react-router-dom'
import { ActionList } from './ActionList'
import { WorkflowTextService } from '@/shared/WorkflowTextService'

export const CourseStep = (props: {
  moduleId: string,
  course: ICourse,
  modules?: ICourseModule[];
  onCommentClick?: (status: COMMENT_STATUS) => void;
  onCourseChanged?: (course: ICourse) => void;
}) => {
  const { moduleId, course, modules } = props

  const module = modules?.find(x => x.id === moduleId);

  const { modified, modifiedBy } = WorkflowService.getLastModified(course, module);

  const isCourseCompleted = WorkflowService.isCourseCompleted(course.CourseStep);
  const isModuleCompleted = WorkflowService.isStepCompleted(course, moduleId);
  const isCreated = modules?.some(x => x.id === moduleId && x.files.some(f => f.name === FILENAMES.RESULTS));
  const isActive = moduleId ? course.CourseStep.startsWith(moduleId) : moduleId === STEPS.STEP1;

  const viewButtonTitle = !!course && !!modules && (isCreated ? "View" : "Preview") && moduleId !== STEPS.COMPLETED;

  const roles = AuthService.getCourseRoles(course);
  const actions = !!course && modules && WorkflowService.getActions(roles, course, moduleId, modules) || [];

  const commentFilter = (comment: IComment) => comment.moduleId === moduleId;

  const opacity = (module || actions.length || (moduleId === STEPS.COMPLETED && isCourseCompleted)) ? 1 : 0.4;

  return (
    <Stack spacing="4" direction="row">
      <Stack spacing="0" align="center">
        <StepCircle isActive={isActive} isCompleted={isModuleCompleted} />
        {moduleId !== STEPS.COMPLETED &&
          <Divider
            orientation="vertical"
            borderWidth="1px"
            borderColor={isModuleCompleted ? 'accent' : 'inherit'}
          />}
      </Stack>
      <Stack w='xl' spacing="0.5" mt="-10" pb='12'>
        <Box as="section" py={{ base: '4', md: '2' }}>
          <Box opacity={opacity} width='full' boxShadow="lg" borderStyle='solid' borderWidth={1} borderColor='inherit' borderRadius="lg" p={{ base: '4', md: '6' }}>
            <VStack w='full' align='start'>
              <HStack w='full' justify='space-between'>
                <HStack spacing="2">
                  {viewButtonTitle
                    ? <Link as={RouterLink} fontSize='x-large' fontWeight={700} to={`/course/${course.id}/${props.moduleId}/view`}>{WorkflowTextService.getModuleName(moduleId)}</Link>
                    : <Text fontSize='x-large' color='fg.muted' fontWeight={700}>{WorkflowTextService.getModuleName(moduleId)}</Text>}
                  {isActive && !isCourseCompleted && <Badge size="sm" colorScheme={WorkflowTextService.getCourseBadgeColor(course.CourseStep)}>{course.CourseStep}</Badge>}
                </HStack>
                <CommentSetBadge course={course} filter={commentFilter} onClick={props.onCommentClick} />
              </HStack>
              <HStack w='full' align='end' justify="space-between">
                <VStack spacing="1" align='start'>
                  <Text textStyle="md">{course && <span dangerouslySetInnerHTML={{ __html: WorkflowService.getStepDescription(course, roles, moduleId) }}></span>}</Text>
                  <Text color='fg.subtle' textStyle="xs">
                    {modified ? `Last modified ${formatDateFromNow(modified)} by ${modifiedBy}` : (moduleId !== STEPS.COMPLETED ? "Not started" : "")}
                  </Text>
                </VStack>
                <ActionList course={course} moduleId={moduleId} actions={actions} onCourseChanged={props.onCourseChanged} />
              </HStack>
            </VStack>
            <CourseStepFileList course={course} module={module} label='Files' />
          </Box>
        </Box>
      </Stack>
    </Stack>
  )
}
